import { useId, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { investingApi } from "../../services/api";
import { parseApiError } from "../../services/apiError";
import { revealLater } from "./lib/reveal";
import useSubmitGuard from "../../hooks/useSubmitGuard";

const MAIN = "h-11 md:h-9";
const REASON_MAX = 1000; // backend/investing/constants.py

/**
 * Retire a thesis, or revive a retired one (spec §2.1, walkthrough 5: a
 * person's, in their own browser — the page shows this only to a person,
 * and the server refuses everyone else with a 403, shown as it says). A
 * reason is required: it's kept in the thesis's revision history.
 *
 * `onSaved(thesis)` gets the thesis's page as the API returned it.
 * Static backdrop; Escape and Cancel are refused while it runs.
 */
function ThesisStatusDialog({ open, thesis, onOpenChange, onSaved, onCloseAutoFocus }) {
  const id = useId();
  const retiring = thesis.status !== "retired";
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const reasonRef = useRef(null);
  const errorRef = useRef(null);
  const submitGuard = useSubmitGuard(open);

  // Each opening starts empty — set while rendering the opening, not in an
  // effect (Radix focuses in its own mount effect, which runs first).
  const [wasOpen, setWasOpen] = useState(false);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setReason("");
      setReasonError(null);
      setError(null);
      setBusy(false);
    }
  }

  const save = async (event) => {
    event.preventDefault();
    // No busy check: the submit button is disabled while it runs.
    const why = reason.trim();
    if (!why) {
      setReasonError("Say why — it's kept in the thesis's revision history.");
      reasonRef.current?.focus();
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const { data } = await investingApi.theses.patch(thesis.slug, {
        status: retiring ? "retired" : "active",
        reason: why,
      });
      setBusy(false);
      onSaved(data);
    } catch (err) {
      setBusy(false);
      const parsed = parseApiError(err, retiring ? "Couldn't retire the thesis." : "Couldn't revive the thesis.");
      if (parsed.fieldErrors.reason) {
        setReasonError([].concat(parsed.fieldErrors.reason).join(" "));
        // Once it's enabled again (it's disabled while busy).
        setTimeout(() => reasonRef.current?.focus(), 0);
      } else {
        revealLater(errorRef);
      }
      const other = Object.entries(parsed.fieldErrors)
        .filter(([name]) => name !== "reason")
        .map(([name, value]) => `${name}: ${[].concat(value).join(" ")}`);
      setError([parsed.formError, ...other].filter(Boolean).join(" — ") || null);
    }
  };

  const verb = retiring ? "Retire" : "Revive";
  return (
    <Dialog open={open} onOpenChange={(next) => onOpenChange(next)}>
      <DialogContent
        data-investing=""
        showCloseButton={false}
        className="flex max-h-[85dvh] flex-col gap-0 p-0 sm:max-w-lg"
        onPointerDownOutside={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
        onEscapeKeyDown={(event) => busy && event.preventDefault()}
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          reasonRef.current?.focus();
        }}
        onCloseAutoFocus={onCloseAutoFocus}
      >
        <DialogHeader className="border-b px-6 py-4 text-left">
          <DialogTitle className="font-display text-xl wrap-anywhere">
            {verb} “{thesis.title}”?
          </DialogTitle>
          <DialogDescription>
            {retiring
              ? "A retired thesis takes no new scores: research stops scoring companies against it, and it can't be a new Own verdict's primary thesis. The scores it already gave stay on the companies' pages, and its text and revision history are kept. You can revive it later."
              : "It takes new scores again — research scores companies against it — and it can be an Own verdict's primary thesis again. Its text, scores and revision history are as they were."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={save} noValidate className="flex min-h-0 flex-1 flex-col">
          <div data-slot="dialog-body" className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-6 py-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={`${id}-reason`}>Reason</Label>
              <Textarea
                ref={reasonRef}
                id={`${id}-reason`}
                value={reason}
                maxLength={REASON_MAX}
                placeholder={
                  retiring
                    ? "e.g. The margin study found no gap between sellers and buyers"
                    : "e.g. The new quarter's margins bring it back"
                }
                className="max-h-48 min-h-20"
                disabled={busy}
                aria-invalid={reasonError ? true : undefined}
                aria-describedby={`${id}-reason-help${reasonError ? ` ${id}-reason-error` : ""}`}
                onChange={(event) => {
                  setReason(event.target.value);
                  setReasonError(null);
                }}
              />
              <p id={`${id}-reason-help`} className="m-0 text-xs text-muted-foreground">
                Required. Kept with this change in the revision history.
              </p>
              {reasonError && (
                <p id={`${id}-reason-error`} className="m-0 text-sm text-destructive">
                  {reasonError}
                </p>
              )}
            </div>
            {error && (
              <Alert ref={errorRef} variant="destructive" role="alert" tabIndex={-1} className="outline-none">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
          </div>
          <DialogFooter className="border-t px-6 py-4">
            <Button type="button" variant="outline" className={MAIN} disabled={busy} onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" variant={retiring ? "destructive" : "default"} className={MAIN} disabled={busy} {...submitGuard}>
              {busy && <Loader2 className="animate-spin" aria-hidden="true" />}
              {busy ? `${retiring ? "Retiring" : "Reviving"}…` : `${verb} thesis`}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

ThesisStatusDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  thesis: PropTypes.shape({
    slug: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
  }).isRequired,
  onOpenChange: PropTypes.func.isRequired,
  /** `(thesis)`: saved; the thesis's page as the API returned it. */
  onSaved: PropTypes.func.isRequired,
  /** Radix's close autofocus: the page moves focus when the opener went away. */
  onCloseAutoFocus: PropTypes.func,
};

export default ThesisStatusDialog;
